import React, { useState, useEffect } from 'react';
import { submitKYC, getKYCStatus, resubmitKYC } from '../services/api';
import toast from 'react-hot-toast';
import { DocumentArrowUpIcon, CameraIcon } from '@heroicons/react/24/outline';

const KYCUpload = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    country: '',
    id_type: 'passport',
  });
  const [idDocument, setIdDocument] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [submission, setSubmission] = useState(null);

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const response = await getKYCStatus();
      if (response.data.success) {
        setKycStatus(response.data.kyc_status);
        setSubmission(response.data.submission);
      }
    } catch (error) {
      console.error('Failed to fetch KYC status');
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        toast.error('Only JPEG and PNG images are allowed');
        return;
      }
      if (type === 'id') {
        setIdDocument(file);
      } else {
        setSelfie(file);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name || !formData.date_of_birth || !formData.country) {
      toast.error('Please fill all fields');
      return;
    }

    if (!idDocument || !selfie) {
      toast.error('Please upload both ID document and selfie');
      return;
    }

    const uploadData = new FormData();
    uploadData.append('full_name', formData.full_name);
    uploadData.append('date_of_birth', formData.date_of_birth);
    uploadData.append('country', formData.country);
    uploadData.append('id_type', formData.id_type);
    uploadData.append('id_document', idDocument);
    uploadData.append('selfie', selfie);

    setLoading(true);
    try {
      const response = await submitKYC(uploadData);
      if (response.data.success) {
        toast.success('KYC submitted successfully! Awaiting admin review.');
        fetchKYCStatus();
        setIdDocument(null);
        setSelfie(null);
        setFormData({
          full_name: '',
          date_of_birth: '',
          country: '',
          id_type: 'passport',
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResubmit = async () => {
    if (!formData.full_name || !formData.date_of_birth || !formData.country) {
      toast.error('Please fill all fields');
      return;
    }

    if (!idDocument || !selfie) {
      toast.error('Please upload both ID document and selfie');
      return;
    }

    const uploadData = new FormData();
    uploadData.append('full_name', formData.full_name);
    uploadData.append('date_of_birth', formData.date_of_birth);
    uploadData.append('country', formData.country);
    uploadData.append('id_type', formData.id_type);
    uploadData.append('id_document', idDocument);
    uploadData.append('selfie', selfie);

    setLoading(true);
    try {
      const response = await resubmitKYC(uploadData);
      if (response.data.success) {
        toast.success('KYC resubmitted successfully! Awaiting admin review.');
        await fetchKYCStatus();
        setIdDocument(null);
        setSelfie(null);
        setFormData({
          full_name: '',
          date_of_birth: '',
          country: '',
          id_type: 'passport',
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Resubmission failed');
    } finally {
      setLoading(false);
    }
  };

  if (kycStatus === 'approved') {
    return (
      <div className="dashboard-page fade-in">
        <div className="kyc-status-shell">
          <div className="kyc-status-card approved">
            <div className="kyc-status-icon approved">
              <svg
                className="h-8 w-8 text-emerald-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="kyc-status-title">KYC Approved</h3>
            <p className="kyc-status-text">
              Your identity has been verified. You can now deposit and trade.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (kycStatus === 'pending' && submission) {
    return (
      <div className="dashboard-page fade-in">
        <div className="kyc-status-shell">
          <div className="kyc-status-card pending">
            <div className="kyc-status-icon pending">
              <svg className="w-7 h-7 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="kyc-status-title">KYC Pending Review</h3>
            <p className="kyc-status-text">Your documents are being reviewed by our team.</p>
            <p className="kyc-status-date">
              Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (kycStatus === 'rejected') {
    return (
      <div className="dashboard-page fade-in">
        <div className="dashboard-head">
          <h1 className="dashboard-page-title">KYC Verification</h1>
          <p className="dashboard-section-subtitle">Resubmit your identity documents</p>
        </div>

        <section className="card dashboard-section kyc-rejection-banner">
          <div className="dashboard-section-body">
            <div className="kyc-status-card rejected slim">
              <div className="kyc-status-icon rejected">
                <svg className="w-7 h-7 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="kyc-status-title">KYC Rejected</h3>
              <p className="kyc-status-text">
                Reason: {submission?.rejection_reason || 'Please resubmit with clear documents'}
              </p>
            </div>
          </div>
        </section>

        <section className="card dashboard-section">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Resubmit KYC Documents</h2>
            <p className="dashboard-section-subtitle">Provide clear and valid identification files</p>
          </div>

          <div className="dashboard-section-body">
            <div className="kyc-form-stack">
              <div className="kyc-grid">
                <div className="auth-field">
                  <label className="auth-label">Full Name (as on ID)</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="auth-input"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div className="auth-field">
                  <label className="auth-label">Date of Birth</label>
                  <input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="auth-input"
                    required
                  />
                </div>

                <div className="auth-field">
                  <label className="auth-label">Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="auth-input"
                    placeholder="United States"
                    required
                  />
                </div>

                <div className="auth-field">
                  <label className="auth-label">ID Type</label>
                  <select
                    value={formData.id_type}
                    onChange={(e) => setFormData({ ...formData, id_type: e.target.value })}
                    className="auth-input"
                  >
                    <option value="passport">Passport</option>
                    <option value="drivers_license">Driver's License</option>
                    <option value="national_id">National ID</option>
                  </select>
                </div>
              </div>

              <div className="kyc-grid">
                <div>
                  <label className="auth-label">ID Document (Front)</label>
                  <div
                    className="kyc-upload-card"
                    onClick={() => document.getElementById('resubmit-id-doc').click()}
                  >
                    <DocumentArrowUpIcon className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                    <p className="kyc-upload-title">
                      {idDocument ? idDocument.name : 'Click to upload ID document'}
                    </p>
                    <p className="kyc-upload-meta">JPEG, PNG (Max 5MB)</p>
                  </div>
                  <input
                    id="resubmit-id-doc"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={(e) => handleFileChange(e, 'id')}
                    className="hidden"
                  />
                </div>

                <div>
                  <label className="auth-label">Selfie (Holding ID)</label>
                  <div
                    className="kyc-upload-card"
                    onClick={() => document.getElementById('resubmit-selfie').click()}
                  >
                    <CameraIcon className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                    <p className="kyc-upload-title">
                      {selfie ? selfie.name : 'Click to upload selfie with ID'}
                    </p>
                    <p className="kyc-upload-meta">JPEG, PNG (Max 5MB)</p>
                  </div>
                  <input
                    id="resubmit-selfie"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={(e) => handleFileChange(e, 'selfie')}
                    className="hidden"
                  />
                </div>
              </div>

              <button
                onClick={handleResubmit}
                className="auth-submit-btn kyc-submit-btn"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Resubmit KYC'}
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="dashboard-page fade-in">
      <div className="dashboard-head">
        <h1 className="dashboard-page-title">KYC Verification</h1>
        <p className="dashboard-section-subtitle">
          Complete identity verification to unlock deposits and trading
        </p>
      </div>

      <section className="card dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">Identity Information</h2>
          <p className="dashboard-section-subtitle">
            To comply with regulations and ensure platform security, please complete identity verification.
          </p>
        </div>

        <div className="dashboard-section-body">
          <div className="kyc-form-stack">
            <div className="kyc-grid">
              <div className="auth-field">
                <label className="auth-label">Full Name (as on ID)</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="auth-input"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="auth-field">
                <label className="auth-label">Date of Birth</label>
                <input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="auth-input"
                  required
                />
              </div>

              <div className="auth-field">
                <label className="auth-label">Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="auth-input"
                  placeholder="United States"
                  required
                />
              </div>

              <div className="auth-field">
                <label className="auth-label">ID Type</label>
                <select
                  value={formData.id_type}
                  onChange={(e) => setFormData({ ...formData, id_type: e.target.value })}
                  className="auth-input"
                >
                  <option value="passport">Passport</option>
                  <option value="drivers_license">Driver's License</option>
                  <option value="national_id">National ID</option>
                </select>
              </div>
            </div>

            <div className="kyc-grid">
              <div>
                <label className="auth-label">ID Document (Front)</label>
                <div
                  className="kyc-upload-card"
                  onClick={() => document.getElementById('id-doc').click()}
                >
                  <DocumentArrowUpIcon className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                  <p className="kyc-upload-title">
                    {idDocument ? idDocument.name : 'Click to upload ID document'}
                  </p>
                  <p className="kyc-upload-meta">JPEG, PNG (Max 5MB)</p>
                </div>
                <input
                  id="id-doc"
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleFileChange(e, 'id')}
                  className="hidden"
                />
              </div>

              <div>
                <label className="auth-label">Selfie (Holding ID)</label>
                <div
                  className="kyc-upload-card"
                  onClick={() => document.getElementById('selfie').click()}
                >
                  <CameraIcon className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                  <p className="kyc-upload-title">
                    {selfie ? selfie.name : 'Click to upload selfie with ID'}
                  </p>
                  <p className="kyc-upload-meta">JPEG, PNG (Max 5MB)</p>
                </div>
                <input
                  id="selfie"
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleFileChange(e, 'selfie')}
                  className="hidden"
                />
              </div>
            </div>

            <button
              onClick={handleSubmit}
              className="auth-submit-btn kyc-submit-btn"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit KYC'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KYCUpload;